/**
 * Autenticación con Google mediante service account, firmando el JWT con WebCrypto.
 *
 * WebCrypto y no una librería de JWT a propósito: es la única API criptográfica disponible en
 * Cloudflare Workers, y está en Node desde la 16. El mismo código corre en los dos lados sin
 * ninguna dependencia.
 *
 * El access token se cachea hasta un minuto antes de vencer. Sin eso, cada request al
 * Worker pagaría un round trip extra a oauth2.googleapis.com.
 */

import { ErrorAdaptador } from './adaptador.ts';

export interface CredencialesServicio {
  client_email: string;
  private_key: string;
  token_uri?: string;
}

export const SCOPES = [
  'https://www.googleapis.com/auth/spreadsheets',
  // drive.file limita el acceso a los archivos que la app creó o que le compartieron
  // explícitamente: el service account no puede ver el resto del Drive del usuario.
  'https://www.googleapis.com/auth/drive.file',
].join(' ');

const TOKEN_URI_POR_DEFECTO = 'https://oauth2.googleapis.com/token';

function base64url(bytes: Uint8Array): string {
  let binario = '';
  for (const b of bytes) binario += String.fromCharCode(b);
  return btoa(binario).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
}

const textoABase64url = (s: string): string => base64url(new TextEncoder().encode(s));

/** PEM PKCS#8 → ArrayBuffer. Tolera `\n` escapados, que es como viene el JSON en un .env. */
function pemADer(pem: string): ArrayBuffer {
  const limpio = pem
    .replaceAll('\\n', '\n')
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s/g, '');
  if (!limpio) throw new ErrorAdaptador('La private_key del service account está vacía.');

  const binario = atob(limpio);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes.buffer;
}

export class AutenticadorGoogle {
  #cred: CredencialesServicio;
  #fetch: typeof fetch;
  #clave: CryptoKey | null = null;
  #token: { valor: string; expiraEn: number } | null = null;
  /** Evita que N requests concurrentes pidan N tokens al mismo tiempo. */
  #enVuelo: Promise<string> | null = null;

  constructor(cred: CredencialesServicio, fetchImpl?: typeof fetch) {
    if (!cred.client_email) throw new ErrorAdaptador('Falta client_email en las credenciales.');
    if (!cred.private_key) throw new ErrorAdaptador('Falta private_key en las credenciales.');
    this.#cred = cred;
    // `bind` obligatorio, no estilo: guardado como campo, `this.#fetch(...)` lo invoca con la
    // instancia como `this`, y en workerd el `fetch` global exige su receptor propio — tira
    // `TypeError: Illegal invocation`. Node lo tolera, así que el bug sólo aparece en Cloudflare:
    // login andaba en `npm run dev` y fallaba con un 500 opaco en producción.
    this.#fetch = fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get email(): string {
    return this.#cred.client_email;
  }

  async token(): Promise<string> {
    const ahora = Math.floor(Date.now() / 1000);
    if (this.#token && this.#token.expiraEn > ahora + 60) return this.#token.valor;
    if (this.#enVuelo) return this.#enVuelo;

    this.#enVuelo = this.#pedirToken(ahora).finally(() => {
      this.#enVuelo = null;
    });
    return this.#enVuelo;
  }

  async #pedirToken(ahora: number): Promise<string> {
    const tokenUri = this.#cred.token_uri ?? TOKEN_URI_POR_DEFECTO;
    const assertion = await this.#firmarJWT(ahora, tokenUri);

    const res = await this.#fetch(tokenUri, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
        assertion,
      }),
    });

    if (!res.ok) {
      const cuerpo = await res.text().catch(() => '');
      throw new ErrorAdaptador(
        'Google rechazó las credenciales del service account.',
        res.status,
        cuerpo.slice(0, 300),
      );
    }

    const datos = (await res.json()) as { access_token?: string; expires_in?: number };
    if (!datos.access_token) {
      throw new ErrorAdaptador('Google no devolvió access_token.', res.status);
    }

    this.#token = {
      valor: datos.access_token,
      expiraEn: ahora + (datos.expires_in ?? 3600),
    };
    return this.#token.valor;
  }

  async #firmarJWT(ahora: number, tokenUri: string): Promise<string> {
    this.#clave ??= await crypto.subtle.importKey(
      'pkcs8',
      pemADer(this.#cred.private_key),
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    const cabecera = textoABase64url(JSON.stringify({ alg: 'RS256', typ: 'JWT' }));
    const claims = textoABase64url(
      JSON.stringify({
        iss: this.#cred.client_email,
        scope: SCOPES,
        aud: tokenUri,
        exp: ahora + 3600,
        iat: ahora,
      }),
    );

    const sinFirmar = `${cabecera}.${claims}`;
    const firma = await crypto.subtle.sign(
      'RSASSA-PKCS1-v1_5',
      this.#clave,
      new TextEncoder().encode(sinFirmar),
    );
    return `${sinFirmar}.${base64url(new Uint8Array(firma))}`;
  }
}

/** Acepta el JSON del service account como objeto o como string. */
export function parsearCredenciales(entrada: string | object): CredencialesServicio {
  const obj = typeof entrada === 'string' ? (JSON.parse(entrada) as Record<string, unknown>) : (entrada as Record<string, unknown>);
  const client_email = obj['client_email'];
  const private_key = obj['private_key'];
  if (typeof client_email !== 'string' || typeof private_key !== 'string') {
    throw new ErrorAdaptador(
      'El JSON del service account no tiene client_email y private_key. Descargalo de la consola de Google Cloud sin editarlo.',
    );
  }
  return {
    client_email,
    private_key,
    ...(typeof obj['token_uri'] === 'string' ? { token_uri: obj['token_uri'] } : {}),
  };
}
